import React, { useState, useEffect, useMemo } from "react";
import { Redirect } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "./PageLayout.scss";
import SecondaryMenu, { SecondaryMenuItems } from "../../components/secondary-menu/SecondaryMenu";
import Navbar from "../../components/navbar/Navbar";
import MainMenu from "../../components/main-menu/MainMenu";
import UploadStatusOverview from "../../components/upload-status-overview/UploadStatusOverview";
import SnackbarNotification from "../../components/snackbar-notifications/SnackbarNotifications";
import OrganizationCollaborationStoppedNotification from "../../components/organization-collaboration-stopped/OrganizationCollaborationStoppedNotification";
import usePageTitle from "../../services/page-title/PageTitleKey";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";
import DownloadProgressSnackbar from "../../components/buttons/download-sewer-objects/DownloadProgressSnackbar";
import useMainMenuStorage from "../../services/main-menu/MainMenuStorageService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import {
  TranslationContext,
  translationService,
} from "../../services/translation/TranslationService";
import { currentUserService } from "../../services/user/CurrentUserStorage";
import ModalNotifications from "../../components/data-components/modal/ModalNotifications";
import { WarningLeavePage } from "../../components/data-components/modal/WarningLeavePage";
import BatchHighlightDetectionStatusOverview from "../../components/batch-processing/BatchHighlightDetectionStatusOverview";
import BatchVideoAnalysisStatusOverview from "../../components/batch-processing/BatchVideoAnalysisStatusOverview";

interface PageLayoutType {
  accessWithOneOfThePermissions?: number[];
  accessWithAllOfThePermissions?: number[];
  id?: string;
  titleKey?: string;
  titleContext?: TranslationContext;
  menuItems?: SecondaryMenuItems;
  children?: any;
  isLoading?: boolean;
}

export default observer(function PageLayout(props: PageLayoutType) {
  const {
    accessWithOneOfThePermissions,
    accessWithAllOfThePermissions,
    id,
    titleKey = "",
    titleContext,
    menuItems = "",
    isLoading,
  } = { ...props };
  const { setPageTitleKey, setPageTitleContext } = usePageTitle();
  const { getMainMenuOpenState } = useMainMenuStorage();
  const [openMainMenu, setOpenMainMenu] = useState(() => {
    return getMainMenuOpenState();
  });
  const [openSecondaryMenu, setOpenMenu] = useState(false);

  useEffect(() => {
    setPageTitleKey(titleKey);
    setPageTitleContext(titleContext);
  }, [setPageTitleKey, setPageTitleContext, titleContext, titleKey]);

  const hasAccessToOrganization = useMemo(() => currentUserService.userIsPartOfAnOrganization, []);

  if (
    accessWithOneOfThePermissions &&
    !permissionsService.userHasOneOfThePermissions(accessWithOneOfThePermissions)
  ) {
    return <Redirect to="/404" />;
  }

  if (
    accessWithAllOfThePermissions &&
    !permissionsService.userHasAllOfThePermissions(accessWithAllOfThePermissions)
  ) {
    return <Redirect to="/404" />;
  }

  const pageLayoutMarkup = (
    <>
      <div className="page-layout" id={id}>
        {titleKey && (
          <div id="page-title" className="page-header">
            <h2 className="page-title">
              {titleContext
                ? translationService.translate(titleKey, titleContext)
                : translationService.translate(titleKey)}
            </h2>
          </div>
        )}

        <SecondaryMenu onMenuToggle={setOpenMenu} {...menuItems} />

        <div
          className={
            "main-content " +
            (menuItems &&
              (openSecondaryMenu ? "open-secondary-menu-margin" : "closed-secondary-menu-margin"))
          }
        >
          {isLoading && <LoadingSpinner />}

          {!isLoading && props.children}
        </div>
      </div>
      <div className="snackbar-info">
        <SnackbarNotification />
        <UploadStatusOverview />
        <BatchVideoAnalysisStatusOverview />
        <BatchHighlightDetectionStatusOverview />
        <DownloadProgressSnackbar />
        <OrganizationCollaborationStoppedNotification />
      </div>
      <ModalNotifications />
      <WarningLeavePage />
    </>
  );

  return (
    <>
      <Navbar hasAccessToOrganization={hasAccessToOrganization} />
      {hasAccessToOrganization ? <MainMenu onMenuToggle={setOpenMainMenu} /> : <React.Fragment />}
      {hasAccessToOrganization ? (
        <div className={`${openMainMenu ? "open-main-menu-margin" : "closed-main-menu-margin"}`}>
          {pageLayoutMarkup}
        </div>
      ) : (
        pageLayoutMarkup
      )}
    </>
  );
});
